/**
 * Phone-number normalization for the dialer.
 *
 * Reps paste numbers copied from other systems, and they arrive in every
 * shape: "+972525276833", "055-9909090", "055-9-90-90-90", "05:2 527 6833".
 * All of those are the same number, and the dialer should accept them
 * without the rep having to retype anything.
 *
 * The rule matches what the server already does (see normalizePhone in
 * server/routes/trengo.ts and matchKeyOf in PhoneContext): keep digits,
 * turn the 972 country code into a local leading 0.
 */

/**
 * Normalize a pasted or typed number to the local dialable form.
 *
 * - strips spaces, dashes, dots, slashes, brackets and any other separator
 * - "+972 52-527-6833" / "00972525276833" → "0525276833"
 * - leaves a number that is already local ("0525276833") untouched
 * - returns '' for input with no digits at all
 *
 * `*` and `#` survive because they are dialable (IVR, feature codes), but
 * only when the value contains no separators to clean — a pasted number is
 * never a feature code.
 */
export function normalizePhoneNumber(raw: string): string {
  if (!raw) return '';

  const trimmed = raw.trim();

  // Feature codes and short DTMF strings (*21#, #31#) pass through as-is.
  if (/^[*#\d]+$/.test(trimmed) && /[*#]/.test(trimmed)) return trimmed;

  let digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';

  // 00 international prefix → drop it, so 00972... is handled like +972...
  if (digits.startsWith('00')) digits = digits.slice(2);

  // Israeli country code → local 0 form. Guard on length so a local number
  // that merely happens to start with 972 isn't mangled.
  if (digits.startsWith('972') && digits.length > 9) {
    digits = '0' + digits.slice(3);
  }

  return digits;
}

/**
 * Should this input be normalized on paste/blur?
 *
 * Only when it looks like a number — a name being typed into the same field
 * must not have its letters stripped.
 */
export function looksLikePhoneNumber(raw: string): boolean {
  if (!raw) return false;
  // No Hebrew or Latin letters, and at least a few digits to work with.
  if (/[א-תa-zA-Z]/.test(raw)) return false;
  return raw.replace(/\D/g, '').length >= 7;
}
